<template>
  <div class="timeline-playback-slider">
    <div class="slider-container">
      <el-slider
        v-model="slider.value"
        :min="slider.min"
        :max="slider.max"
        :format-tooltip="formatTooltip"
        :disabled="isDisabled"
      />
    </div>
    <div class="playback-descriptions">
      <span>{{ $t("statsTimeline.playbackSlider.start") }}</span>
      <span>{{ sliderTime }}</span>
      <span>{{ $t("statsTimeline.playbackSlider.end") }}</span>
    </div>

    <div class="action-buttons">
      <div class="playback-btns">
        <el-button
          size="mini"
          class="base-btn skip-btn"
          @click="skipBack()"
          :disabled="isDisabled"
          :title="$t('statsTimeline.playbackSlider.skipBack')"
        >
          <img src="@/assets/skip-back.svg" alt="Skip Back"
        /></el-button>
        <el-button
          type="primary"
          @click="handlePlayPause()"
          class="base-btn play-btn"
          :disabled="isDisabled"
          :title="
            isPlaying
              ? $t('statsTimeline.playbackSlider.pause')
              : $t('statsTimeline.playbackSlider.play')
          "
        >
          <template v-if="isPlaying">
            <img src="@/assets/pause.svg" alt="Pause" />
          </template>
          <template v-else>
            <img src="@/assets/play.svg" alt="Play" />
          </template>
        </el-button>
        <el-button
          size="mini"
          class="base-btn skip-btn"
          @click="skipFoward()"
          :disabled="isDisabled"
          :title="$t('statsTimeline.playbackSlider.skipFoward')"
        >
          <img src="@/assets/skip-foward.svg" alt="Skip Foward"
        /></el-button>
      </div>
      <el-button
        size="mini"
        class="base-btn speed-btn"
        :disabled="isDisabled"
        @click="changeSpeed"
        :title="$t('tooltip.animationSpeed')"
      >
        {{ speedLabel }}
      </el-button>
    </div>
  </div>
</template>

<script>
export default {
  name: "TimelinePlaybackSlider",
  props: {
    sliderOptions: {
      type: Object,
      default: () => ({})
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    resetPlayback: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      slider: {
        value: 0,
        min: 0,
        max: 10,
        step: 1,
        marks: {},
        speed: 1000
      },
      isPlaying: false,
      speedOptions: [
        { speedLabel: "0.5x", speed: 2000 },
        { speedLabel: "1x", speed: 1000 },
        { speedLabel: "2x", speed: 500 }
      ]
    };
  },
  computed: {
    sliderTime() {
      return this.slider.marks?.[this.slider.value]?.label || "00:00:00";
    },
    speedLabel() {
      const speedOption = this.speedOptions.find(
        option => option.speed === this.slider.speed
      );
      return speedOption ? speedOption.speedLabel : "1x";
    }
  },
  methods: {
    formatTooltip(value) {
      if (this.sliderOptions.marks && this.sliderOptions.marks[value]?.label) {
        return this.sliderOptions.marks[value]?.label;
      }
    },
    skipBack() {
      if (this.slider.value > this.slider.min) {
        this.slider.value -= this.slider.step;
      }
    },
    skipFoward() {
      if (this.slider.value < this.slider.max) {
        this.slider.value += this.slider.step;
      }
    },
    startPlayback() {
      this.playbackInterval = setInterval(() => {
        if (this.slider.value < this.slider.max) {
          this.slider.value += this.slider.step;
        } else {
          this.slider.value = this.slider.min;
        }
      }, this.slider.speed);
    },
    handlePlayPause() {
      this.isPlaying = !this.isPlaying;
      if (this.isPlaying) {
        this.startPlayback();
      } else {
        clearInterval(this.playbackInterval);
      }
    },
    changeSpeed() {
      const currentIndex = this.speedOptions.findIndex(
        option => option.speed === this.slider.speed
      );
      const nextIndex = (currentIndex + 1) % this.speedOptions.length;
      this.slider.speed = this.speedOptions[nextIndex].speed;

      if (this.isPlaying) {
        clearInterval(this.playbackInterval);
        this.startPlayback();
      }
    }
  },
  watch: {
    sliderOptions: {
      deep: true,
      handler(newOptions) {
        if (!newOptions) return;
        if (newOptions.min !== undefined) {
          this.slider.min = newOptions.min;
        }
        if (newOptions.max !== undefined) {
          this.slider.max = newOptions.max;
        }
        if (newOptions.value !== undefined) {
          this.slider.value = newOptions.value;
        }
        if (newOptions.step !== undefined) {
          this.slider.step = newOptions.step;
        }
        if (newOptions.marks !== undefined) {
          this.slider.marks = newOptions.marks;
        }
      },
      resetPlayback(newOptions) {
        if (newOptions) {
          this.isPlaying = false;
          clearInterval(this.playbackInterval);
          this.slider.min = 0;
          this.slider.max = 1;
          this.slider.value = this.slider.min;
          this.slider.marks = {};
        }
      }
    },
    "slider.value"(newValue) {
      this.$emit("slider-changed", newValue);
    }
  }
};
</script>

<style lang="scss">
.timeline-playback-slider {
  margin-bottom: 42px;
  .el-slider {
    width: 100%;
  }
  .action-buttons {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    position: relative;
    margin-top: 16px;

    .playback-btns {
      display: flex;
      justify-content: center;
      gap: 8px;

      .base-btn {
        margin-left: 0 !important;
        padding: 0;
        width: 40px;
        height: 40px;
      }
      .skip-btn {
        border: none;
      }
    }

    .speed-btn {
      border: none;
      position: absolute;
      right: 0;
    }
  }
  .playback-descriptions {
    display: flex;
    justify-content: space-between;
    font-size: 0.75rem;
    line-height: 1rem;
    color: #606266;
  }
  .slider-container {
    display: flex;
    align-items: center;
    gap: 8px;
  }
}
</style>
